import axios from "axios";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CREATE_jwel } from "../../../Config/Config";

/* ════════════════════════════════════════════════════════════════
   DATA LAYER  (no UI here)
   Source: PURCHINVOICE_MAST only (same table, no detail table).
   Chart = one bar per PType, bar height = sum of TOTQTY (null → 0).
   Always shows TODAY's data only.
   Change only this block if the names differ.
   ════════════════════════════════════════════════════════════════ */
const PURCHASE_SOURCE = {
  endpoint: "/api/ERP/GetDataFromGivenTableNameWithWhere", // ⚠ CONFIRM exact endpoint name
  table: "PURCHINVOICE_MAST",
  dateKey: "INVDATE",
  typeKey: "PType",
  qtyKey: "TOTQTY",
  amountKey: "netamt", // tooltip only
  cancelKey: "cancelled",
};

const normalizeType = (name) =>
  !name || name === "undefined" || String(name).trim() === "-"
    ? "UNASSIGNED"
    : String(name).trim().toUpperCase();

const inRange = (row, src, from, to) => {
  const d = String(row[src.dateKey] || "").slice(0, 10);
  return d >= from && d <= to;
};

const aggregateByType = (rows, src) => {
  const groups = rows.reduce((acc, item) => {
    const key = normalizeType(item[src.typeKey]);
    const g = acc[key] || { qty: 0, amount: 0, bills: 0 };
    g.qty += Number(item[src.qtyKey] || 0); // null → 0
    g.amount += Number(item[src.amountKey] || 0);
    g.bills += 1;
    acc[key] = g;
    return acc;
  }, {});
  return Object.entries(groups)
    .map(([ptype, g]) => ({ ptype, ...g }))
    .sort((a, b) => b.qty - a.qty);
};

const usePurchasesByType = (from, to) => {
  const tenantName = localStorage.getItem("tenantName");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const src = PURCHASE_SOURCE;
    const nextDay = dayjs(to).add(1, "day").format("YYYY-MM-DD");
    const where = `${src.dateKey}>='${from}' AND ${src.dateKey}<'${nextDay}'`;

    setLoading(true);
    setError(null);
    axios
      .get(
        `${CREATE_jwel}${src.endpoint}?tableName=${src.table}&where=${where}`,
        { headers: { tenantName } },
      )
      .then((res) => {
        const rows = (res.data ?? []).filter(
          (item) =>
            String(item[src.cancelKey] || "").toUpperCase() !== "YES" &&
            inRange(item, src, from, to),
        );
        setData(aggregateByType(rows, src));
      })
      .catch((err) => {
        console.error("CommodityWisePurchases fetch error:", err);
        setError("Couldn't load purchase data");
        setData([]);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  return { data, loading, error };
};

/* ════════════════════════════════════════════════════════════════
   UI
   ════════════════════════════════════════════════════════════════ */

const TYPE_COLORS = {
  GOLD: "#e0a526",
  SILVER: "#9aa5b1",
  PLATINUM: "#74b9ff",
  DIAMOND: "#a29bfe",
  UNASSIGNED: "#b2bec3",
};
const FALLBACK_COLORS = [
  "#6c5ce7",
  "#00cec9",
  "#fd79a8",
  "#00b894",
  "#e17055",
  "#81ecec",
];
const colorFor = (ptype, i) =>
  TYPE_COLORS[ptype] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];

const fmtAmt = (v) =>
  v >= 100000
    ? `₹${(v / 100000).toFixed(1)}L`
    : v >= 1000
      ? `₹${(v / 1000).toFixed(0)}k`
      : `₹${Number(v).toFixed(0)}`;

const fmtQty = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
};

const CTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div
      style={{
        background: "rgba(30,20,80,0.93)",
        borderRadius: 10,
        padding: "8px 12px",
        color: "#fff",
        fontSize: 11,
        boxShadow: "0 4px 16px rgba(44,27,105,0.3)",
        minWidth: 150,
      }}
    >
      <div style={{ fontWeight: 800, marginBottom: 3, fontSize: 12 }}>
        {label}
      </div>
      {[
        ["Qty", fmtQty(d.qty)],
        ["Amount", fmtAmt(d.amount)],
        ["Bills", d.bills],
      ].map(([k, v]) => (
        <div
          key={k}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 14,
            marginBottom: 2,
          }}
        >
          <span style={{ color: "rgba(255,255,255,0.6)" }}>{k}</span>
          <strong>{v}</strong>
        </div>
      ))}
    </div>
  );
};

/**
 * CommodityWisePurchases chart card.
 * Always shows today's purchases (no date picker, no props).
 */
const CommodityWisePurchases = () => {
  // Today only — string dates keep the hook's dependency array stable
  const today = dayjs().format("YYYY-MM-DD");
  const { data, loading, error } = usePurchasesByType(today, today);

  const barW = data.length > 8 ? 10 : 14;
  const chartW = Math.max(data.length * (barW + 22), 260);
  const totalQty = data.reduce((a, d) => a + d.qty, 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      style={{
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 4px 20px rgba(44,27,105,0.10)",
        padding: "12px 12px 8px",
        height: 280,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 8,
          flexShrink: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#2d1b69" }}>
            Commodity-wise Purchases
          </div>
          <div style={{ fontSize: 9, color: "#aaa", marginTop: 1 }}>
            {data.length} types · Today · {dayjs(today).format("DD/MM/YYYY")}
          </div>
        </div>

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.15 }}
          style={{
            background: "linear-gradient(135deg,#2d1b69,#7c5cbf)",
            borderRadius: 8,
            padding: "3px 9px",
            textAlign: "center",
            minWidth: 52,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              fontSize: 8,
              color: "rgba(255,255,255,0.7)",
              fontWeight: 600,
            }}
          >
            TOTAL QTY
          </div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#fff" }}>
            {fmtQty(totalQty)}
          </div>
        </motion.div>
      </div>

      <div
        style={{
          flex: 1,
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: "#b8a4e8 #f5f0ff",
          display: "flex",
          alignItems: "center",
          justifyContent: data.length === 0 ? "center" : "flex-start",
        }}
      >
        {loading ? (
          <div style={{ fontSize: 11, color: "#aaa" }}>Loading…</div>
        ) : error ? (
          <div style={{ fontSize: 11, color: "#e17055" }}>{error}</div>
        ) : data.length === 0 ? (
          <div style={{ fontSize: 11, color: "#aaa" }}>
            No purchases today
          </div>
        ) : (
          <div style={{ width: chartW, height: 210 }}>
            <ResponsiveContainer width="100%" height={210}>
              <BarChart
                data={data}
                margin={{ top: 18, right: 6, left: 2, bottom: 30 }}
                barCategoryGap="35%"
              >
                <CartesianGrid
                  stroke="#f0eaff"
                  strokeDasharray="2 3"
                  vertical={false}
                />
                <XAxis
                  dataKey="ptype"
                  tick={{ fontSize: 9, fill: "#4c3494", fontWeight: 600 }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  angle={-38}
                  textAnchor="end"
                  dy={4}
                />
                <YAxis
                  tick={{ fontSize: 9, fill: "#aaa" }}
                  axisLine={false}
                  tickLine={false}
                  width={30}
                  allowDecimals={false}
                />
                <Tooltip
                  content={<CTooltip />}
                  cursor={{ fill: "rgba(108,92,231,0.07)" }}
                />
                <Bar
                  dataKey="qty"
                  radius={[4, 4, 0, 0]}
                  barSize={barW}
                  animationDuration={800}
                  label={{
                    position: "top",
                    fontSize: 8,
                    fill: "#4c3494",
                    fontWeight: 700,
                    formatter: (v) => (v > 0 ? fmtQty(v) : ""),
                  }}
                >
                  {data.map((d, i) => (
                    <Cell key={d.ptype} fill={colorFor(d.ptype, i)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default CommodityWisePurchases;